import type { NewEvent } from '@ilteducation/activity-stream-types';
import { storeToRefs } from 'pinia';
import { sendEventToActivityStream } from './api';
import eventCacheStore from './offline-event-cache';

const BATCH_LIMIT = 25; // a somewhat random limit...
const TIMEOUT_SECONDS = 10;
let batch: NewEvent[] = [];
let sendAttempt = 1;

let timeout: NodeJS.Timeout | undefined;

const flushAndClearTimeout = async () => {
  const cacheStore = eventCacheStore();
  const { cache } = storeToRefs(cacheStore);
  clearTimeout(timeout);
  const tmpEvents = batch;
  batch = [];
  const events = tmpEvents.concat(cache.value as unknown as NewEvent[]);
  if (events.length > 0) {
    try {
      await sendEventToActivityStream(events);
      timeout = undefined;
      sendAttempt = 1;
      cacheStore.clear();
    } catch (error) {
      sendAttempt += 1;
      console.error(`Failed to send events to activity stream, ${sendAttempt}`, error);

      cacheStore.addAll(tmpEvents);
      // incrementally increase the delay between attempts
      if (!timeout) {
        // increase the timeout up to a max of 100s
        timeout = setTimeout(flushAndClearTimeout, TIMEOUT_SECONDS * (sendAttempt > 10 ? 10 : sendAttempt) * 1000);
      }
    }
  }
};

// batch events to reduce the api load
const addEvents = (events: NewEvent[], flush?: boolean): void => {
  batch.push(...events);

  if (batch.length === BATCH_LIMIT || flush) {
    flushAndClearTimeout();
  } else if (!timeout) {
    timeout = setTimeout(flushAndClearTimeout, TIMEOUT_SECONDS * (sendAttempt > 10 ? 10 : sendAttempt) * 1000);
  }
};

export const sendEvents = (events: NewEvent[], flush?: boolean): void => {
  /**
   *   This is a TEMPORARY workaround to fix broken epub playback on library.
   *   It's not clear for now how to inject JWT authorization token to external axios call from NUXTed app,
   *   which currently causing an issue.
   *   TODO Remove me.
   */
  const filtered = events.filter((event) => event.sourceApplication !== 'education');
  if (filtered.length === 0) {
    return;
  }

  addEvents(events, flush);
};

export const sendEvent = (event: NewEvent, flush?: boolean): void => sendEvents([event], flush);

export const flush = async () => {
  flushAndClearTimeout();
};
